import * as React from 'react';
import styled, { keyframes, css, DefaultTheme, Keyframes } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { NotificationType, INotificationProps, NotificationPosition } from './notification-types';
import { NotificationItemContent, NotificationItemActionButton, CloseButton } from './parts';
import { NotificationMessageTypes } from './theme';

const getNotificationMessageItemStylesByType = memoizeFunction((type: NotificationMessageTypes) => {
    const getStyles = (theme: DefaultTheme) => {
        const { notificationMessage: { colors } } = theme;
        return colors[type];
    };
    return css`
        border-color: ${({ theme }) => getStyles(theme).borderColor};
        background-color: ${({ theme }) => getStyles(theme).backgroundColor};
        color: ${({ theme }) => getStyles(theme).textColor};
    `;
});

function getStyleType(type: NotificationType = 'information') {
    switch (type) {
        case 'error':
            return getNotificationMessageItemStylesByType('error');
        case 'information':
            return getNotificationMessageItemStylesByType('information');
        case 'success':
            return getNotificationMessageItemStylesByType('success');
        case 'warning':
            return getNotificationMessageItemStylesByType('warning');
        default:
            return getNotificationMessageItemStylesByType('information');
    }
}

const animationConfig: { [Key: string]: { enter: Keyframes, exit: Keyframes } } = {
    topLeft: {
        enter: keyframes`
                    from { transform: translateY(-80%); }
                    to { transform: translateY(0%); }
                `,
        exit: keyframes`
                    from { transform: translateX(0%); }
                    to { transform: translateX(-100%); }
                `
    },
    topRight: {
        enter: keyframes`
                    from { transform: translateY(-80%); }
                    to { transform: translateY(0%); }
                `,
        exit: keyframes`
                    from { transform: translateX(0%); }
                    to { transform: translateX(100%); }
                `
    },
    bottomLeft: {
        enter: keyframes`
                    from { transform: translateX(-100%); }
                    to { transform: translateX(0%); }
                `,
        exit: keyframes`
                    from { transform: translateX(0%); }
                    to { transform: translateX(-100%); }
                `
    },
    bottomRight: {
        enter: keyframes`
                    from { transform: translateX(100%); }
                    to { transform: translateX(0%); }
                `,
        exit: keyframes`
                    from { transform: translateX(0%); }
                    to { transform: translateX(100%); }
                `
    }
};

const Wrapper = styled.div.attrs({
    className: String('ap-notification-itemWrapper')
}) <{ $isUnmounting?: boolean; $type?: NotificationType; $position: NotificationPosition; }>`
    position: relative;
    box-sizing: border-box;
    width: 100%;
    cursor: default;
    border-style: solid;
    overflow: hidden;
    max-height: 200px;
    margin: ${({ theme: { semantics: { gap } } }) => gap.xxxl};
    border-radius: ${({ theme: { notificationMessage: { composites } } }) => composites.borderRadius};
    border-width: ${({ theme: { notificationMessage: { composites } } }) => composites.borderWidth};
    ${({ $type }) => getStyleType($type)};
    ${({ $isUnmounting, $position }) => ($isUnmounting ?
        css`animation: ${animationConfig[$position].exit} 0.4s ease-out;` :
        css`animation: ${animationConfig[$position].enter} 0.25s ease-in;`)}
`;

interface INotificationItemState {
    isUnmounting: boolean;
}

export class NotificationItem extends React.PureComponent<INotificationProps, INotificationItemState> {

    static defaultProps = {
        type: 'information'
    };

    private autoCloseTimeout?: number;
    private unmountingTimeout?: number;

    constructor(props: INotificationProps) {
        super(props);
        this.state = {
            isUnmounting: false
        };
    }

    render() {
        const { className, position, type, onRenderCloseIcon } = this.props;
        const { isUnmounting } = this.state;
        return (
            <Wrapper $type={type} className={className} $isUnmounting={isUnmounting} $position={position} role="alert">
                {this.renderContent()}
                <CloseButton onClick={this.onCloseClick} onRenderCloseIcon={onRenderCloseIcon} />
                {this.renderActionButton()}
            </Wrapper>
        );
    }

    componentDidMount() {
        this.setAutoClose();
    }

    componentWillUnmount() {
        clearTimeout(this.autoCloseTimeout);
        clearTimeout(this.unmountingTimeout);
    }

    private renderContent() {
        const { additionalInfo, showActionButton, message, type } = this.props;
        return (
            <NotificationItemContent additionalInfo={additionalInfo}
                getOnRenderNotificationIcon={this.getOnRenderIcon} isActionButtonVisible={showActionButton}
                message={message} type={type} />
        );
    }

    private renderActionButton() {
        const { actionLabel = 'Action', onActionButtonClick, type, showActionButton } = this.props;
        return showActionButton ? (
            <NotificationItemActionButton actionLabel={actionLabel} onClick={onActionButtonClick} type={type!} />
        ) : null;
    }

    private getOnRenderIcon = () => {
        const { type, onRenderInformationIcon, onRenderErrorIcon, onRenderSuccessIcon, onRenderWarningIcon } = this.props;
        switch (type) {
            case 'information':
                return onRenderInformationIcon;
            case 'error':
                return onRenderErrorIcon;
            case 'success':
                return onRenderSuccessIcon;
            case 'warning':
                return onRenderWarningIcon;
            default:
                return undefined;
        }
    }

    private setAutoClose() {
        const { timeOut } = this.props;
        const timeOutToSet = timeOut !== undefined ? timeOut : this.getDefaultTimeOut();

        if (timeOutToSet > 0) {
            this.autoCloseTimeout = setTimeout(this.onCloseClick, timeOutToSet * 1000);
        }
    }

    private getDefaultTimeOut() {
        const { type, getDefaultConfigs } = this.props;
        const defaultConfigs = getDefaultConfigs();
        switch (type) {
            case 'information': return defaultConfigs.defaultInformationTimeOut;
            case 'error': return defaultConfigs.defaultErrorTimeOut;
            case 'success': return defaultConfigs.defaultSuccessTimeOut;
            case 'warning': return defaultConfigs.defaultWarningTimeOut;
            default: return defaultConfigs.defaultInformationTimeOut;
        }
    }

    private onCloseClick = () => {
        const { id = '', onClose } = this.props;
        this.setState({ isUnmounting: true });
        this.unmountingTimeout = setTimeout(onClose, 360, id);
    }
}
